import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  MenuItem,
  Typography,
  TextField,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { addDays, format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  InvoiceEdit,
  PaymentTerm,
  TYPE_EXTERNAL,
  TYPE_INTERNAL,
} from "../invoiceAPI";

import { validateDate } from "../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import {
  DesktopDatePickerLight,
  SelectLight,
  TextFieldLight,
} from "../../../common/components/styled/styled";
import {
  dateMonthYearFormat,
  isoFormat,
  leadingZerosDateFormat,
} from "../../../common/helpers/formatDateTime";
import { getCoreUserFullName } from "../../../common/utils/users";
import { CustomerContract } from "../../customer/customerAPI";
import {
  fetchExpertUsers,
  selectAllExpertUsers,
} from "../../user/expertUsersSlice";
import { CoreUser } from "../../user/types/entities";
import { InvoiceSubtype } from "../constants";
import { fetchPaymentTerms } from "../invoiceDetailedSlice";
import { VerticalAlignGrid } from "../styled";
import {
  getInvoiceSubtype,
  isInvoiceCreditInvoice,
  isInvoicePartOfCreditInvoiceBundle,
} from "../utils";
import {
  fetchCustomerUsersByCompanyId,
  selectAllCustomerUsers,
} from "../../customer/customerUsersSlice";
import { CustomerUser } from "../../customer/types/queries";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { fetchCustomerCompanyByOrgId } from "../../customer/customerOrganizationsSlice";

interface Props {
  invoice: InvoiceEdit;
  customerContracts: CustomerContract[];
  updateInfo: (
    invoiceInfo: {
      invoiceDate: string;
      sellerReferenceIdentifier: string;
      buyerReferenceIdentifier: string;
      orderIdentifier: string;
      agreementIdentifier: string;
      contractId: string | undefined;
      invoiceDueDate: string;
      invoiceValueDate: string;
      paymentTermDays: number;
      salesPersonId?: string;
      buyerContactPersonName?: string;
      originalInvoiceNumber: string | null;
    },
    isValid: boolean,
  ) => void;
}

export const InvoiceInformation = ({
  invoice,
  customerContracts,
  updateInfo,
}: Props) => {
  const { t } = useTranslation("invoices");
  const dispatch = useAppDispatch();

  const customerUsers = useAppSelector(selectAllCustomerUsers);
  const customerUserOptions = useMemo(() => {
    return customerUsers.map((user: CustomerUser) => ({
      id: user.id,
      name: getCoreUserFullName(user),
    }));
  }, [customerUsers]);

  const expertUsers = useAppSelector((state) => selectAllExpertUsers(state));
  const expertUserOptions = useMemo(() => {
    return expertUsers.map((user: CoreUser) => ({
      id: user.id,
      name: getCoreUserFullName(user),
    }));
  }, [expertUsers]);

  const invoiceContract = useMemo(() => {
    return customerContracts.find(
      (contract) => contract.id === invoice.contractId,
    );
  }, [customerContracts, invoice.contractId]);

  const isNewInvoice = useMemo(
    () => !invoice.invoiceNumber,
    [invoice.invoiceNumber],
  );
  const hasDeliveryDateActivated =
    invoiceContract?.invoiceDeliveryDate || false;

  const [paymentTermOptions, setPaymentTermOptions] = useState<PaymentTerm[]>(
    [],
  );

  const [date, setDate] = useState<Date>(
    invoice.invoiceDate ? new Date(invoice.invoiceDate) : new Date(),
  );
  const [sellerReference, setSellerReference] = useState<string>(
    invoice.sellerReferenceIdentifier || "",
  );
  const [buyerReference, setBuyerReference] = useState<string>(
    invoice.buyerReferenceIdentifier || "",
  );

  const [orderReference, setOrderReference] = useState<string>(
    invoice.orderIdentifier || "",
  );
  const [agreementReference, setAgreementReference] = useState<string>(
    invoice.agreementIdentifier || "",
  );
  const [contractId, setContractId] = useState<string>(
    invoice?.contractId || "",
  );
  const [dueDate, setDueDate] = useState<Date>(
    invoice.invoiceDueDate ? new Date(invoice.invoiceDueDate) : new Date(),
  );
  const [valueDate, setValueDate] = useState<Date | null>(
    invoice.invoiceValueDate ? new Date(invoice.invoiceValueDate) : null,
  );
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(
    invoice.deliveryDate ? new Date(invoice.deliveryDate) : null,
  );
  const [paymentTermDays, setPaymentTermDays] = useState<number>(
    parseInt(invoice?.paymentTermDays) || 0,
  );
  const [buyerContactPerson, setBuyerContactPerson] =
    useState<AutocompleteType | null>(
      invoice.buyerContactPersonId && invoice.buyerContactPersonName
        ? {
            id: invoice.buyerContactPersonId,
            name: invoice.buyerContactPersonName,
          }
        : null,
    );
  const [sellerContactPerson, setSellerContactPerson] =
    useState<AutocompleteType | null>(
      invoice.salesPersonId && invoice.salesPersonName
        ? {
            id: invoice.salesPersonId,
            name: invoice.salesPersonName,
          }
        : null,
    );
  const [originalInvoiceNumber, setOriginalInvoiceNumber] = useState<
    string | null
  >(invoice.originalInvoiceNumber);
  const [invoiceSubtype, setInvoiceSubtype] = useState<string>(
    getInvoiceSubtype(invoice),
  );

  const hasDeliveryDateError = (date: Date | null) => {
    if (date) {
      return !validateDate(date, dateMonthYearFormat);
    }
    return false;
  };

  const selectPaymentTermDays = (event: any) =>
    setPaymentTermDays(event.target.value);

  const selectCustomerContract = (event: any) => {
    const contractId = event.target.value;
    const value: CustomerContract[] = customerContracts.filter(
      (option) => option.id === contractId,
    );
    setContractId(value[0].id);
    setPaymentTermDays(value[0].paymentTermDays || 0);
  };

  const selectInvoiceSubtype = (event: any) => {
    setInvoiceSubtype(event.target.value);
  };

  useEffect(() => {
    if (date) {
      const newDueDate = addDays(new Date(date), paymentTermDays);
      setDueDate(newDueDate);
    }
  }, [date, paymentTermDays]);

  useEffect(() => {
    const info = {
      invoiceDate: validateDate(date, isoFormat) ? format(date, isoFormat) : "",
      sellerReferenceIdentifier: sellerReference,
      buyerReferenceIdentifier: buyerReference,
      orderIdentifier: orderReference,
      agreementIdentifier: agreementReference,
      contractId,
      invoiceDueDate: validateDate(dueDate, isoFormat)
        ? format(dueDate, isoFormat)
        : "",
      invoiceValueDate:
        valueDate && validateDate(valueDate, isoFormat)
          ? format(valueDate, isoFormat)
          : "",
      deliveryDate,
      paymentTermDays,
      salesPersonId: sellerContactPerson?.id,
      buyerContactPersonId: buyerContactPerson?.id,
      buyerContactPersonName: buyerContactPerson?.name,
      originalInvoiceNumber,
    };
    const infoValid =
      !!info.invoiceDate &&
      !!info.invoiceValueDate &&
      info.paymentTermDays.toString() !== "" &&
      (invoice.invoiceType === TYPE_EXTERNAL ? !!info.contractId : true) &&
      (invoiceSubtype !== InvoiceSubtype.SalesInvoice
        ? !!originalInvoiceNumber
        : true) &&
      !hasDeliveryDateError(deliveryDate);
    updateInfo(info, infoValid);
  }, [
    date,
    sellerReference,
    buyerReference,
    orderReference,
    agreementReference,
    contractId,
    dueDate,
    valueDate,
    deliveryDate,
    paymentTermDays,
    invoice.invoiceType,
    buyerContactPerson,
    sellerContactPerson,
    invoiceSubtype,
    originalInvoiceNumber,
    updateInfo,
  ]);

  useEffect(() => {
    dispatch(fetchPaymentTerms({})).then(
      (action) =>
        isFulfilled(action) &&
        setPaymentTermOptions(action.payload.paymentTerms),
    );
  }, [dispatch]);

  const appliedSalesPersonId = useRef<string | null>(null);

  useEffect(() => {
    dispatch(fetchExpertUsers({}));
  }, [dispatch]);

  // For new invoices set default seller/Eezy contact person from contract if available
  useEffect(() => {
    if (!isNewInvoice) return;
    const salesPersonId = invoiceContract?.salesPersonId;
    if (!salesPersonId || appliedSalesPersonId.current === salesPersonId) {
      return;
    }
    const defaultEezyContactPerson = expertUserOptions.find(
      (user) => user.id === salesPersonId,
    );
    if (!defaultEezyContactPerson) return;
    appliedSalesPersonId.current = salesPersonId;
    setSellerContactPerson(defaultEezyContactPerson);
  }, [expertUserOptions, invoiceContract?.salesPersonId, isNewInvoice]);

  const fetchCustomerUsersForCompany = useCallback(
    (customerCompanyId: string) => {
      dispatch(
        fetchCustomerUsersByCompanyId({
          customerCompanyId,
        }),
      ).then((action) => {
        // For new invoices set default buyer contact person from contract if available
        if (isFulfilled(action) && isNewInvoice) {
          const customerUsers = action.payload.map((user: CustomerUser) => ({
            id: user.id,
            name: getCoreUserFullName(user),
          }));
          const defaultBuyerContactPerson = customerUsers.find(
            (user) =>
              user.id === invoiceContract?.invoicingResponsiblePerson?.id,
          );
          setBuyerContactPerson(defaultBuyerContactPerson || null);
        }
      });
    },
    [isNewInvoice, invoiceContract?.invoicingResponsiblePerson, dispatch],
  );

  // Fetch customer users for buyer contact person options
  useEffect(() => {
    if (invoice.invoiceType === TYPE_INTERNAL) return;
    if (!invoice.customerCompanyId) {
      if (!invoice.customerOrganizationId) return;
      dispatch(
        fetchCustomerCompanyByOrgId({
          customerOrgId: invoice.customerOrganizationId,
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          fetchCustomerUsersForCompany(action.payload.customerCompanyId);
        }
      });
    } else {
      fetchCustomerUsersForCompany(invoice.customerCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    invoice.customerCompanyId,
    invoice.invoiceType,
    invoice.customerOrganizationId,
    dispatch,
  ]);

  const isCreditInvoiceBundleItem = isInvoicePartOfCreditInvoiceBundle(invoice);
  const isCreditInvoiceItem = isInvoiceCreditInvoice(invoice);

  return (
    <Box sx={{ marginBottom: "40px" }}>
      <Typography
        variant="header"
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {t("invoiceInformation")}
      </Typography>
      <Grid container sx={{ marginTop: "20px" }}>
        <Grid item xs={4}>
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4}>
              <Typography align="right">{t("date")}</Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <DesktopDatePickerLight
                  format={leadingZerosDateFormat}
                  label={null}
                  value={date}
                  disabled={!!invoice.invoiceWave || isCreditInvoiceItem}
                  onChange={(newValue: any) => {
                    if (newValue) {
                      setDate(
                        validateDate(newValue, dateMonthYearFormat)
                          ? newValue
                          : "",
                      );
                    }
                  }}
                  slotProps={{ textField: { size: "small" } }}
                />
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4}>
              <Typography align="right">{t("sellerReference")}</Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <TextFieldLight
                  size="small"
                  value={sellerReference}
                  onChange={(event) => setSellerReference(event.target.value)}
                />
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4}>
              <Typography align="right">{t("buyerReference")}</Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <TextFieldLight
                  size="small"
                  value={buyerReference}
                  onChange={(event) => setBuyerReference(event.target.value)}
                />
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4}>
              <Typography align="right">{t("orderReference")}</Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <TextFieldLight
                  size="small"
                  value={orderReference}
                  onChange={(event) => setOrderReference(event.target.value)}
                />
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4}>
              <Typography align="right">{t("agreementReference")}</Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <TextFieldLight
                  size="small"
                  value={agreementReference}
                  onChange={(event) =>
                    setAgreementReference(event.target.value)
                  }
                />
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4} hidden={invoice.invoiceType === TYPE_INTERNAL}>
              <Typography align="right">{t("contract")}</Typography>
            </Grid>
            <Grid item xs={8} hidden={invoice.invoiceType === TYPE_INTERNAL}>
              <FormControl fullWidth size="small">
                <SelectLight
                  value={contractId}
                  disabled={!!invoice.invoiceWave}
                  label={null}
                  onChange={selectCustomerContract}
                >
                  {customerContracts.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </SelectLight>
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
        </Grid>
        <Grid item xs={4}>
          <Grid item container spacing={2} xs={12}>
            <VerticalAlignGrid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("dueDate")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <DesktopDatePickerLight
                    format={leadingZerosDateFormat}
                    label={null}
                    value={dueDate}
                    disabled
                    onChange={(newValue) => {}}
                    slotProps={{ textField: { size: "small" } }}
                  />
                </FormControl>
              </Grid>
            </VerticalAlignGrid>
          </Grid>
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4}>
              <Typography align="right">{t("valueDate")}</Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <DesktopDatePickerLight
                  format={leadingZerosDateFormat}
                  label={null}
                  value={valueDate}
                  disabled={!!invoice.invoiceWave}
                  onChange={(newValue: Date | null) => {
                    setValueDate(
                      newValue && validateDate(newValue, dateMonthYearFormat)
                        ? newValue
                        : null,
                    );
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: !valueDate,
                      required: true,
                    },
                  }}
                />
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4}>
              <Typography align="right">{t("deliveryDate")}</Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <DesktopDatePickerLight
                  format={leadingZerosDateFormat}
                  label={null}
                  value={deliveryDate}
                  disabled={!hasDeliveryDateActivated}
                  onChange={(newValue: any) => {
                    setDeliveryDate(newValue);
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: hasDeliveryDateError(deliveryDate),
                    },
                  }}
                />
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4}>
              <Typography align="right">{t("paymentTerm")}</Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <SelectLight
                  value={paymentTermDays}
                  disabled={!!invoice.invoiceWave}
                  label={null}
                  onChange={selectPaymentTermDays}
                >
                  {paymentTermOptions.map((option) => (
                    <MenuItem key={option.days} value={option.days}>
                      {option.days}
                    </MenuItem>
                  ))}
                </SelectLight>
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          {invoice.invoiceType === TYPE_EXTERNAL && (
            <VerticalAlignGrid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("buyerContactPerson")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <Autocomplete
                    fullWidth
                    value={buyerContactPerson}
                    options={customerUserOptions.sort((a, b) =>
                      a.name.localeCompare(b.name),
                    )}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    onChange={(_, newValue) => {
                      setBuyerContactPerson(newValue);
                    }}
                    renderInput={(params) => <CommonTextField {...params} />}
                  />
                </FormControl>
              </Grid>
            </VerticalAlignGrid>
          )}
          <VerticalAlignGrid item container spacing={2} xs={12}>
            <Grid item xs={4}>
              <Typography align="right">{t("sellerContactPerson")}</Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  fullWidth
                  value={sellerContactPerson}
                  options={expertUserOptions.sort((a, b) =>
                    a.name.localeCompare(b.name),
                  )}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  onChange={(_, newValue) => setSellerContactPerson(newValue)}
                  renderInput={(params) => <CommonTextField {...params} />}
                />
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          {/* In edit mode, display subtype and originalInvoiceNumber for credit invoice bundle items only.
            In create mode, always show editable options. */}
          {isNewInvoice || isCreditInvoiceBundleItem ? (
            <VerticalAlignGrid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("invoiceType")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <SelectLight
                    value={invoiceSubtype}
                    disabled={!isNewInvoice}
                    label={null}
                    onChange={selectInvoiceSubtype}
                  >
                    <MenuItem value={InvoiceSubtype.SalesInvoice}>
                      {t(`${InvoiceSubtype.SalesInvoice}`)}
                    </MenuItem>
                    <MenuItem value={InvoiceSubtype.CreditInvoice}>
                      {t(`${InvoiceSubtype.CreditInvoice}`)}
                    </MenuItem>
                    <MenuItem value={InvoiceSubtype.NewRepairInvoice} disabled>
                      {t(`${InvoiceSubtype.NewRepairInvoice}`)}
                    </MenuItem>
                  </SelectLight>
                </FormControl>
              </Grid>
            </VerticalAlignGrid>
          ) : null}
          {(isNewInvoice && invoiceSubtype !== InvoiceSubtype.SalesInvoice) ||
          isCreditInvoiceBundleItem ? (
            <VerticalAlignGrid item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">
                  {t("originalInvoiceNumber")}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <TextField
                    value={originalInvoiceNumber}
                    disabled={!isNewInvoice}
                    onChange={(event) =>
                      setOriginalInvoiceNumber(event.target.value.trim())
                    }
                    size="small"
                  />
                </FormControl>
              </Grid>
            </VerticalAlignGrid>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
};
